import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  useUserRaces,
  type UserRaceItem,
} from '../context/UserRacesContext';
import { BookmarkButton } from '../races/BookmarkButton';
import { formatKorDate } from '../utils/dateRange';
import { getRegionLabel } from '../utils/regionMapper';
import { formatFinishTime } from '../utils/finishTime';
import './MyRacesPage.css';

type TabKey = 'bookmarked' | 'registered' | 'finished';

const TAB_DEFS: Array<{ key: TabKey; label: string }> = [
  { key: 'bookmarked', label: '북마크' },
  { key: 'registered', label: '참가예정' },
  { key: 'finished', label: '완주기록' },
];

export function MyRacesPage() {
  const { user, loading: authLoading } = useAuth();
  const { items, loading, error } = useUserRaces();
  const [activeTab, setActiveTab] = useState<TabKey>('bookmarked');
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const langPrefix = lang ? `/${lang}` : '';

  const groups = useMemo(() => {
    const bookmarked: UserRaceItem[] = [];
    const registered: UserRaceItem[] = [];
    const finished: UserRaceItem[] = [];
    for (const item of items) {
      if (item.relation === 'bookmarked') bookmarked.push(item);
      else if (item.relation === 'registered') registered.push(item);
      else if (item.relation === 'finished') finished.push(item);
    }
    return { bookmarked, registered, finished };
  }, [items]);

  const openLoginModal = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('login', '1');
    navigate(location.pathname + `?${params.toString()}` + location.hash);
  }, [location.pathname, location.search, location.hash, navigate]);

  if (authLoading) {
    return (
      <div className="my-races-page">
        <div className="my-races-empty">불러오는 중…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-races-page">
        <header className="my-races-header">
          <h1 className="my-races-title">내 대회</h1>
          <p className="my-races-subtitle">
            로그인하면 북마크한 대회, 참가예정, 완주 기록을 관리할 수 있어요.
          </p>
        </header>
        <div className="my-races-empty">
          <div>로그인이 필요합니다.</div>
          <div className="my-races-empty-login">
            <button
              type="button"
              className="my-races-login-btn"
              onClick={openLoginModal}
            >
              로그인
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activeItems = groups[activeTab];

  return (
    <div className="my-races-page">
      <header className="my-races-header">
        <h1 className="my-races-title">내 대회</h1>
        <p className="my-races-subtitle">
          북마크한 대회, 참가예정 대회, 완주 기록을 한 곳에서 확인하세요.
        </p>
      </header>

      <div className="my-races-tabs" role="tablist">
        {TAB_DEFS.map((t) => {
          const count = groups[t.key].length;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`my-races-tab ${isActive ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
              <span className="my-races-tab-count">{count}</span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="my-races-error" role="alert">
          목록을 불러오지 못했습니다: {error}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="my-races-empty">불러오는 중…</div>
      ) : activeItems.length === 0 ? (
        <div className="my-races-empty" data-testid="empty-tab">
          {activeTab === 'bookmarked' && '아직 북마크한 대회가 없습니다.'}
          {activeTab === 'registered' && '등록한 대회가 없습니다.'}
          {activeTab === 'finished' && '완주 기록이 없습니다.'}
        </div>
      ) : (
        <div className="my-races-list" data-testid={`list-${activeTab}`}>
          {activeItems.map((item) => (
            <div key={`${item.relation}-${item.race.id}`} className="my-races-item">
              <div className="my-races-item-main">
                <Link
                  to={`${langPrefix}/races/${encodeURIComponent(item.race.id)}`}
                  className="my-races-item-name"
                >
                  {item.race.nameLocal || item.race.name}
                </Link>
                <div className="my-races-item-meta">
                  {getRegionLabel(item.race.region)} ·{' '}
                  {formatKorDate(item.race.date, 'numeric-weekday')}
                </div>
              </div>
              <div className="my-races-item-side">
                {item.relation === 'finished' ? (
                  <>
                    {item.finishTimeSec !== null && (
                      <span className="my-races-item-time">
                        {formatFinishTime(item.finishTimeSec)}
                      </span>
                    )}
                    {item.distance && <span>{item.distance}</span>}
                  </>
                ) : (
                  <BookmarkButton
                    raceId={item.race.id}
                    raceName={item.race.nameLocal || item.race.name}
                    size="sm"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
